import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";


const API_URL = import.meta.env.VITE_API_URL || "https://eleven11-i6v4.onrender.com";

const REASONS = [
  "Wrong part received",
  "Part does not fit my watercraft",
  "Item arrived damaged",
  "Part is not as described",
  "Changed my mind",
  "Other",
];

export default function SupportScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [tab, setTab] = useState("return");
  const [form, setForm] = useState({
    orderNumber: "",
    reason: "",
    details: "",
    email: user?.email || "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const update = (field, value) =>
    setForm(prev => ({ ...prev, [field]: value }));

const [error, setError] = useState("");

const handleSubmit = async () => {
  if (!form.orderNumber || !form.reason || !form.email) return;
  setSubmitting(true);
  setError("");
  try {
    const res = await fetch(`${API_URL}/api/returns`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...form, userId: user?.id || "" })
    });
    const data = await res.json();
    if (data.success) setSubmitted(true);
    else setError("Submission failed. Please try again.");
  } catch {
    setError("Submission failed. Please try again.");
  } finally {
    setSubmitting(false);
  }
};

  return (
    <div style={styles.page}>
      <div style={styles.container}>

        {/* Header */}
        <div style={styles.header}>
          <button style={styles.back} onClick={() => navigate("/account")}>
            ‹ Back
          </button>
          <p style={styles.kicker}>ELEVEN SUPPORT</p>
          <h1 style={styles.title}>Returns & Support</h1>
          <p style={styles.subtitle}>
            We stand behind every part we sell. Here's how our return process works.
          </p>
        </div>

        {/* Eligibility Cards */}
        <div style={styles.eligibilityGrid}>
          <EligibilityCard
icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 12l3 3 5-5"/></svg>}
            color="#ecfdf5"
            border="#6ee7b7"
            title="Eligible for Return"
            items={[
              "Used condition parts only",
              "Returned within 7 days of delivery",
              "Part in original received condition",
              "Non-electrical components",
            ]}
          />
          <EligibilityCard
  icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>}
            color="#fef2f2"
            border="#fca5a5"
            title="Not Eligible"
            items={[
              "Brand New condition parts",
              "Any electrical components",
              "Parts returned after 7 days",
              "Items showing new damage",
            ]}
          />
        </div>

        {/* How It Works */}
        <div style={styles.card}>
          <p style={styles.sectionLabel}>HOW RETURNS WORK</p>
          <div style={styles.stepsGrid}>
            <Step number="01" title="Submit a Request" desc="Fill out the return form below with your order number and reason." />
            <Step number="02" title="We Review" desc="Our team reviews your request within 1–2 business days." />
            <Step number="03" title="Shipping Label" desc="If approved, we email you a prepaid return shipping label." />
            <Step number="04" title="Store Credit" desc="Once we receive and inspect the part, store credit is issued." />
          </div>
        </div>

        {/* Policy Fine Print */}
        <div style={styles.finePrintCard}>
          <div style={styles.finePrintHeader}>
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#70747e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><line x1="9" y1="12" x2="15" y2="12"/><line x1="9" y1="16" x2="13" y2="16"/></svg>
            <p style={{ margin: 0, fontSize: 12, fontWeight: 900, color: "#70747e", letterSpacing: 2.2 }}>RETURN POLICY — FINE PRINT</p>
          </div>
          <div style={styles.finePrintDivider} />
          <div style={styles.finePrintList}>
            <FinePrintRow
              bold="Return Window"
              text="Returns must be initiated within 7 days of the delivery date shown on your order. Requests submitted after this window will not be accepted under any circumstances."
            />
            <FinePrintRow
              bold="Eligible Items"
              text="Only parts listed in Used condition at the time of purchase are eligible for return. Brand New condition parts are final sale and cannot be returned or exchanged."
            />
            <FinePrintRow
              bold="Electrical Parts — No Exceptions"
              text="Electrical components including but not limited to starters, CDI units, rectifiers, ignition coils, gauges, wiring harnesses, and sensors are strictly non-returnable. No exceptions will be made regardless of condition or circumstance."
            />
            <FinePrintRow
              bold="Restocking Fee"
              text="A 20% restocking fee is applied to all approved returns. This fee is deducted from the store credit issued and covers the cost of the prepaid return shipping label provided by ELEVEN."
            />
            <FinePrintRow
              bold="Return Shipping"
              text="ELEVEN provides a prepaid shipping label for all approved returns. The cost of this label is covered under the 20% restocking fee. Customers are not responsible for return shipping out of pocket."
            />
            <FinePrintRow
              bold="Refund Method"
              text="All approved returns are refunded as ELEVEN store credit only. Store credit has no expiration date and can be applied to any future purchase. Cash refunds, card reversals, and chargebacks are not issued for returned parts."
            />
            <FinePrintRow
              bold="Condition on Return"
              text="Parts must be returned in the same condition as received. Items that arrive with new damage, missing components, or signs of installation attempts beyond what was present at delivery will not be accepted."
            />
            <FinePrintRow
              bold="Abuse & Fraud"
              text="ELEVEN reserves the right to refuse returns from accounts showing patterns of abuse. Fraudulent return requests may result in account suspension and forfeiture of store credit."
            />
          </div>
        </div>

        {/* Tabs */}
        <div style={styles.tabRow}>
          <button
            style={{ ...styles.tabBtn, ...(tab === "return" ? styles.tabActive : {}) }}
            onClick={() => setTab("return")}
          >
            Start a Return
          </button>
          <button
            style={{ ...styles.tabBtn, ...(tab === "contact" ? styles.tabActive : {}) }}
            onClick={() => setTab("contact")}
          >
            Contact Support
          </button>
        </div>

        {/* Return Form */}
        {tab === "return" && (
          <div style={styles.card}>
            <p style={styles.sectionLabel}>RETURN REQUEST</p>

            {submitted ? (
              <div style={styles.successBox}>
                <div style={styles.successIcon}>✓</div>
                <h3 style={styles.successTitle}>Request Submitted</h3>
                <p style={styles.successText}>
                  We'll review your return request and respond within 1–2 business days.
                  If approved, a prepaid shipping label will be sent to <strong>{form.email}</strong>.
                </p>
                <button
                  style={styles.resetBtn}
                  onClick={() => {
                    setSubmitted(false);
                    setForm({ orderNumber: "", reason: "", details: "", email: user?.email || "" });
                  }}
                >
                  Submit Another
                </button>
              </div>
            ) : (
              <>
                <div style={styles.warningBanner}>
       <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#92400e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{display:"inline",marginRight:8,verticalAlign:"middle"}}><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
Returns are only accepted for<strong>Used condition</strong> parts. Electrical parts and Brand New items are not eligible.
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Order Number</label>
                  <input
                    style={styles.input}
                    placeholder="e.g. ORD-2024-00123"
                    value={form.orderNumber}
                    onChange={e => update("orderNumber", e.target.value)}
                  />
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Email Address</label>
                  <input
                    style={styles.input}
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={e => update("email", e.target.value)}
                  />
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Reason for Return</label>
                  <select
                    style={{ ...styles.input, color: form.reason ? "#111" : "#9ca3af" }}
                    value={form.reason}
                    onChange={e => update("reason", e.target.value)}
                  >
                    <option value="" disabled>Select a reason...</option>
                    {REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>
                    Additional Details{" "}
                    <span style={{ color: "#9ca3af", fontWeight: 500 }}>(optional)</span>
                  </label>
                  <textarea
                    style={{ ...styles.input, height: 110, resize: "vertical", paddingTop: 14 }}
                    placeholder="Describe the issue in more detail..."
                    value={form.details}
                    onChange={e => update("details", e.target.value)}
                  />
                </div>

                <div style={styles.feeNote}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0c4a6e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0,marginTop:2}}><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                  <span>A <strong>20% restocking fee</strong> will be deducted from your store credit. This covers your prepaid return shipping label.</span>
                </div>

{error && <p style={{ color: "#dc2626", fontSize: 14, fontWeight: 600, marginBottom: 12 }}>{error}</p>}

                <button
                  style={{
                    ...styles.submitBtn,
                    opacity: (!form.orderNumber || !form.reason || !form.email || submitting) ? 0.5 : 1,
                    cursor: (!form.orderNumber || !form.reason || !form.email) ? "not-allowed" : "pointer",
                  }}
                  onClick={handleSubmit}
                  disabled={!form.orderNumber || !form.reason || !form.email || submitting}
                >
                  {submitting ? "Submitting..." : "Submit Return Request"}
                </button>
              </>
            )}
          </div>
        )}

        {/* Contact */}
        {tab === "contact" && (
          <div style={styles.card}>
            <p style={styles.sectionLabel}>CONTACT SUPPORT</p>
            <div style={styles.contactGrid}>
              <ContactMethod
              icon={<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>}
                title="Email Support"
                value="support@eleven11marine.com"
                sub="Response within 1–2 business days"
                href="mailto:support@eleven11marine.com"
              />
              <ContactMethod
icon={<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>}
                title="Live Chat"
                value="Available on site"
                sub="Mon–Fri, 9am–6pm EST"
                href="#"
              />
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

function EligibilityCard({ icon, color, border, title, items }) {
  return (
    <div style={{ ...styles.eligCard, background: color, border: `1px solid ${border}` }}>
      <div style={styles.eligHeader}>
<div style={styles.eligIcon}>{icon}</div>
        <h3 style={styles.eligTitle}>{title}</h3>
      </div>
      {items.map((item, i) => (
        <div key={i} style={styles.eligItem}>
          <span style={styles.eligDot}>•</span>
          <span style={styles.eligText}>{item}</span>
        </div>
      ))}
    </div>
  );
}

function Step({ number, title, desc }) {
  return (
    <div style={styles.step}>
      <div style={styles.stepNumber}>{number}</div>
      <h4 style={styles.stepTitle}>{title}</h4>
      <p style={styles.stepDesc}>{desc}</p>
    </div>
  );
}

function FinePrintRow({ bold, text }) {
  return (
    <div style={styles.finePrintRow}>
      <div style={styles.finePrintBold}>{bold}</div>
      <div style={styles.finePrintText}>{text}</div>
    </div>
  );
}

function ContactMethod({ icon, title, value, sub, href }) {
  return (
    <a href={href} style={styles.contactCard}>
      <div style={styles.contactIcon}>{icon}</div>
      <div>
        <div style={styles.contactTitle}>{title}</div>
        <div style={styles.contactValue}>{value}</div>
        <div style={styles.contactSub}>{sub}</div>
      </div>
    </a>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(180deg, #f5f7fb 0%, #eef1f7 45%, #f7f8fb 100%)",
    paddingTop: 90,
    paddingBottom: 80,
    fontFamily: "-apple-system, BlinkMacSystemFont, Inter, Helvetica, Arial, sans-serif",
  },
  container: { width: "min(780px, calc(100% - 40px))", margin: "0 auto" },
  header: { marginBottom: 32 },
  back: { background: "none", border: "none", fontSize: 17, fontWeight: 700, color: "#0a84ff", cursor: "pointer", padding: 0, marginBottom: 16, display: "block" },
  kicker: { margin: 0, fontSize: 12, fontWeight: 800, letterSpacing: 2.4, color: "#7b7f89" },
  title: { margin: "6px 0 8px", fontSize: 42, letterSpacing: -1.5, fontWeight: 800, color: "#050505" },
  subtitle: { margin: 0, fontSize: 16, color: "#6b7280", lineHeight: 1.6 },
  eligibilityGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 },
  eligCard: { borderRadius: 22, padding: "24px 26px" },
  eligHeader: { display: "flex", alignItems: "center", gap: 10, marginBottom: 16 },
  eligIcon: { fontSize: 22 },
  eligTitle: { margin: 0, fontSize: 16, fontWeight: 800, color: "#111" },
  eligItem: { display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 8 },
  eligDot: { fontSize: 16, color: "#6b7280", flexShrink: 0, lineHeight: "22px" },
  eligText: { fontSize: 14, color: "#374151", lineHeight: "22px" },
  card: { borderRadius: 28, background: "rgba(255,255,255,0.85)", backdropFilter: "blur(24px)", border: "1px solid rgba(255,255,255,0.8)", boxShadow: "0 26px 60px rgba(24,35,60,0.1)", padding: "32px 36px", marginBottom: 20 },
  sectionLabel: { margin: "0 0 24px", fontSize: 12, fontWeight: 900, color: "#70747e", letterSpacing: 2.2 },
  stepsGrid: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 20 },
  step: { textAlign: "center" },
  stepNumber: { fontSize: 28, fontWeight: 900, color: "#e5e7eb", letterSpacing: -1, marginBottom: 10 },
  stepTitle: { margin: "0 0 8px", fontSize: 15, fontWeight: 800, color: "#111" },
  stepDesc: { margin: 0, fontSize: 13, color: "#6b7280", lineHeight: 1.6 },
  finePrintCard: { borderRadius: 28, background: "rgba(255,255,255,0.85)", backdropFilter: "blur(24px)", border: "1px solid rgba(255,255,255,0.8)", boxShadow: "0 26px 60px rgba(24,35,60,0.1)", padding: "32px 36px", marginBottom: 20 },
  finePrintHeader: { display: "flex", alignItems: "center", gap: 10, marginBottom: 20 },
  finePrintIcon: { fontSize: 20 },
  finePrintDivider: { height: 1, background: "rgba(0,0,0,0.06)", marginBottom: 24 },
  finePrintList: { display: "flex", flexDirection: "column", gap: 0 },
  finePrintRow: { padding: "18px 0", borderTop: "1px solid rgba(0,0,0,0.05)" },
  finePrintBold: { fontSize: 14, fontWeight: 800, color: "#111", marginBottom: 6 },
  finePrintText: { fontSize: 14, color: "#6b7280", lineHeight: 1.7 },
  tabRow: { display: "flex", gap: 10, marginBottom: 20 },
  tabBtn: { flex: 1, padding: "14px", borderRadius: 14, border: "1px solid #E0E3EA", background: "rgba(255,255,255,0.7)", fontSize: 15, fontWeight: 700, color: "#6b7280", cursor: "pointer" },
  tabActive: { background: "#111", color: "#fff", border: "1px solid #111" },
  warningBanner: { background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 12, padding: "12px 16px", fontSize: 14, color: "#92400e", marginBottom: 24, lineHeight: 1.6 },
  inputGroup: { marginBottom: 20 },
  label: { display: "block", fontSize: 13, fontWeight: 700, color: "#6b7280", marginBottom: 8 },
  input: { width: "100%", padding: "14px 18px", borderRadius: 14, border: "1px solid #E0E3EA", background: "#fff", fontSize: 16, fontWeight: 500, color: "#111", outline: "none", boxSizing: "border-box", fontFamily: "inherit" },
  feeNote: { display: "flex", alignItems: "flex-start", gap: 10, background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 12, padding: "12px 16px", fontSize: 14, color: "#0c4a6e", marginBottom: 24, lineHeight: 1.6 },
  feeIcon: { fontSize: 16, flexShrink: 0 },
  submitBtn: { width: "100%", padding: "17px", borderRadius: 999, border: "none", background: "#111", color: "#fff", fontSize: 16, fontWeight: 800, cursor: "pointer" },
  successBox: { textAlign: "center", padding: "20px 0" },
  successIcon: { width: 64, height: 64, borderRadius: "50%", background: "#ecfdf5", border: "2px solid #6ee7b7", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, color: "#059669", margin: "0 auto 20px", fontWeight: 900 },
  successTitle: { margin: "0 0 12px", fontSize: 24, fontWeight: 800 },
  successText: { fontSize: 15, color: "#6b7280", lineHeight: 1.7, margin: "0 0 24px" },
  resetBtn: { background: "none", border: "1px solid #E0E3EA", borderRadius: 999, padding: "12px 24px", fontSize: 14, fontWeight: 700, color: "#374151", cursor: "pointer" },
  contactGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 },
  contactCard: { display: "flex", alignItems: "flex-start", gap: 16, padding: "20px", borderRadius: 18, border: "1px solid #E0E3EA", background: "#fafafa", textDecoration: "none" },
  contactIcon: { fontSize: 28, flexShrink: 0 },
  contactTitle: { fontSize: 13, fontWeight: 800, color: "#6b7280", marginBottom: 4, textTransform: "uppercase", letterSpacing: 1 },
  contactValue: { fontSize: 16, fontWeight: 700, color: "#111", marginBottom: 4 },
  contactSub: { fontSize: 13, color: "#9ca3af" },
};